import useSettings from "../hooks/useSettings";
import useSessionTimeout from "../hooks/useSessionTimeout";
import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useToast, ToastContainer } from "../hooks/useToast";
import BlackoutPicker from "../components/BlackoutPicker";
import { TIER_COLORS } from "../constants/tiers";
import { fireConfetti } from "../utils/celebrate";
import Countdown from "../components/Countdown";

const MAROON = "#6f0000";
const DARK = "#1C0A0A";
const BLANK_ATTENDEE = { name: "", email: "", dietaryRestrictions: "" };

// Convert "HH:MM" (24hr legacy) or already-formatted "H:MM AM/PM" to "H:MM AM/PM"
function fmt12(t) {
  if (!t) return "";
  if (/am|pm/i.test(t)) return t;
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function Modal({ onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 16, padding: 32, maxWidth: 560, width: "90%", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.3)", animation: "fadeUp 0.3s ease" }}>
        {children}
      </div>
    </div>
  );
}

export default function BoardPortal() {
  const { settings } = useSettings();
  const boardWeekDays = settings.boardWeek?.days || [];
  function tierInterviewDaysLabel(tier) {
    const labels = boardWeekDays.filter(d => (d.events || []).some(e => e.enabled && e.type === "interviews" && e.tier === tier)).map(d => d.label);
    return labels.length > 0 ? labels.join(" – ") + " interviews" : "Interviews TBD";
  }
  const boardWeekStart = (() => {
    if (boardWeekDays.length === 0) return null;
    const yr = settings.programYear || new Date().getFullYear().toString();
    const m = boardWeekDays[0].label && boardWeekDays[0].label.match(/(\w+ \d+)\s*$/);
    if (!m) return null;
    const d = new Date(`${m[1]} ${yr}`);
    return isNaN(d.getTime()) ? null : d;
  })();
  const [activeTab, setActiveTab] = useState("profile");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [candidateGroupFilter, setCandidateGroupFilter] = useState("all");
  const [candidateSort, setCandidateSort] = useState("default");
  const [blockedStudents, setBlockedStudents] = useState([]);
  const [blockoutTimes, setBlockoutTimes] = useState([]);
  const [blockoutRequestStatus, setBlockoutRequestStatus] = useState("");
  const [preferences, setPreferences] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [mySchedule, setMySchedule] = useState([]);
  const [boardAnnouncements, setBoardAnnouncements] = useState([]);
  const [, setDismissedAnnouncements] = useState([]);
  const [schedulePublished, setSchedulePublished] = useState(false);
  const [presentationsPublished, setPresentationsPublished] = useState(false);
  const [presentationSchedule, setPresentationSchedule] = useState([]);
  const [boardFerpaSignedURL, setBoardFerpaSignedURL] = useState("");
  const [boardFerpaChecked, setBoardFerpaChecked] = useState(false);
  const [boardFerpaSubmitting, setBoardFerpaSubmitting] = useState(false);
  const [platinumPrefsLocked, setPlatinumPrefsLocked] = useState(false);
  const [goldPrefsLocked, setGoldPrefsLocked] = useState(false);
  const [silverPrefsLocked, setSilverPrefsLocked] = useState(false);
  const [resumeBooksPublished, setResumeBooksPublished] = useState(false);
  const [resumeBooks, setResumeBooks] = useState([]);
  const [websiteLocked, setWebsiteLocked] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRsvp, setSavingRsvp] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const { toasts, showToast, dismiss } = useToast();
  const [previewStudent, setPreviewStudent] = useState(null);
  const [additionalAttendees, setAdditionalAttendees] = useState([{ ...BLANK_ATTENDEE }]);
  const [profile, setProfile] = useState({
    companyName: "", mainContactFirst: "", mainContactLast: "",
    email: "", phone: "", memberType: "",
    totalAttendees: "", dietaryRestrictions: "",
    address: "", springInternshipsOffered: "", summerInternshipsOffered: "",
    interviewRooms: "1",
    aug18breakfast: false, aug18interviews: false, aug18lunch: false, aug18mondayDinner: false,
    aug19breakfast: false, aug19interviews: false, aug19lunch: false, aug19tuesdayDinner: false,
    aug20goldBreakfast: false, aug20goldInterviews: false, aug20goldLunch: false, aug20wednesdayDinner: false, goldDraft: false,
    aug22breakfast: false, aug22silverInterviews: false, aug22lunch: false,
  });
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Run once on mount only — loadStudents/loadExisting are redefined every render, so adding them as deps would re-trigger on every update
  useEffect(() => {
    Promise.all([loadStudents(), loadExisting()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsaved) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsaved]);

  async function loadStudents() {
    const snap = await getDocs(collection(db, "students"));
    setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function loadExisting() {
    if (!user) return;
    // Load user record to get company name
    const userSnap = await getDoc(doc(db, "users", user.uid));
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const companyName = userData.displayEmail || userData.name || "";
      setProfile(p => ({ ...p, companyName: companyName || p.companyName }));
    }
    // Load publish status — single read, check all flags including new ones
    const pubSnap = await getDoc(doc(db, "metadata", "publishStatus"));
    let schedPub = false;
    if (pubSnap.exists()) {
      const pub = pubSnap.data();
      setResumeBooksPublished(pub.resumeBooks || false);
      schedPub = pub.scheduleBoard || false;
      setSchedulePublished(schedPub);
      setPlatinumPrefsLocked(pub.platinumPrefsLocked || false);
      setGoldPrefsLocked(pub.goldPrefsLocked || false);
      setSilverPrefsLocked(pub.silverPrefsLocked || false);
      setWebsiteLocked(pub.websiteLocked || false);
      if (pub.presentationsBoard) {
        const presSnap = await getDoc(doc(db, "metadata", "presentationSchedule"));
        if (presSnap.exists()) setPresentationSchedule(presSnap.data().entries || []);
        setPresentationsPublished(true);
      }
    }
    // Load resume books
    const rbSnap = await getDoc(doc(db, "metadata", "resumeBooks"));
    if (rbSnap.exists()) setResumeBooks(rbSnap.data().books || []);
    // Load board preferences
    const snap = await getDoc(doc(db, "boardPreferences", user.uid));
    if (snap.exists()) {
      const data = snap.data();
      setPreferences(data.preferences || []);
      setSubmitted(data.submitted || false);
      if (data.profile) setProfile(p => ({ ...p, ...data.profile }));
      if (data.ferpaConsent) setFerpaConsent(data.ferpaConsent);
      if (data.ferpaSignedURL) setBoardFerpaSignedURL(data.ferpaSignedURL);
      if (data.additionalAttendees?.length > 0) setAdditionalAttendees(data.additionalAttendees);
      setBlockedStudents(data.blockedStudents || []);
      setBlockoutTimes(data.blockoutTimes || []);
      setBlockoutRequestStatus(data.blockoutRequestStatus || "");
      setDismissedAnnouncements(data.dismissedAnnouncements || []);
      // Load company schedule when published
      const companyName = (data.profile?.companyName || "").trim();
      if (schedPub && companyName) {
        const schedSnap = await getDoc(doc(db, "metadata", "scheduleData"));
        if (schedSnap.exists()) {
          const schedData = schedSnap.data().companySchedules || {};
          const myKey = Object.keys(schedData).find(k => k === companyName) ||
                        Object.keys(schedData).find(k => k.toLowerCase() === companyName.toLowerCase()) ||
                        Object.keys(schedData).find(k => k.toLowerCase().includes(companyName.toLowerCase()) || companyName.toLowerCase().includes(k.toLowerCase()));
          if (myKey) setMySchedule(schedData[myKey]);
        }
      }
    }
    // Load announcements — only ones meant for everyone or specifically for this
    // board member (recipientUid), and not already dismissed by them.
    try {
      const dismissedIds = new Set(snap.exists() ? (snap.data().dismissedAnnouncements || []) : []);
      const annSnap = await getDocs(collection(db, "announcements"));
      const now2 = new Date();
      const boardAnns = annSnap.docs.map(d=>({id:d.id,...d.data()})).filter(a =>
        a.active && (a.target==="board"||a.target==="all") &&
        (!a.expiresAt||new Date(a.expiresAt)>now2) &&
        (!a.recipientUid || a.recipientUid===user.uid) &&
        !dismissedIds.has(a.id)
      );
      setBoardAnnouncements(boardAnns.sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    } catch(e) {}
  }

  async function dismissAnnouncement(id) {
    setBoardAnnouncements(prev => prev.filter(a => a.id !== id));
    setDismissedAnnouncements(prev => {
      const next = [...prev, id];
      setDoc(doc(db, "boardPreferences", user.uid), { dismissedAnnouncements: next }, { merge: true }).catch(()=>{});
      return next;
    });
  }

  function updateProfile(key, value) {
    setProfile(p => ({ ...p, [key]: value }));
    setUnsaved(true);
  }

  function handleMemberTypeSelect(type) {
    const defaults = {
      "Platinum Board Member": { interviewRooms: "1" },
      "Gold Board Member": { interviewRooms: "1" },
      "Silver Board Member": { interviewRooms: "1" },
    };
    setProfile(p => ({ ...p, memberType: type, ...defaults[type] }));
    setUnsaved(true);
  }

  function switchTab(id) {
    if (unsaved) {
      if (!window.confirm("You have unsaved changes. Leave without saving?")) return;
      setUnsaved(false);
    }
    setActiveTab(id);
  }

  async function saveProfile(e) {
    e && e.preventDefault();
    if (!profile.mainContactFirst || !profile.email || !profile.memberType) {
      showToast("Please fill in contact first name, email, and board member type before saving", "error");
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, "boardPreferences", user.uid), {
        profile, additionalAttendees, preferences, submitted, ferpaConsent,
        updatedAt: new Date().toISOString(), submittedBy: user.email
      }, { merge: true });
      showToast("Profile saved successfully", "success");
      setUnsaved(false);
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setSaving(false);
  }

  async function saveRsvp() {
    setSavingRsvp(true);
    try {
      await setDoc(doc(db, "boardPreferences", user.uid), {
        profile, additionalAttendees, preferences, submitted, ferpaConsent,
        updatedAt: new Date().toISOString(), submittedBy: user.email
      }, { merge: true });
      showToast("RSVP saved", "success");
      setUnsaved(false);
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setSavingRsvp(false);
  }

  async function saveBlockout() {
    setSaving(true);
    try {
      await setDoc(doc(db, "boardPreferences", user.uid), {
        blockoutTimes, blockoutRequestStatus: "pending", blockoutRequestedAt: new Date().toISOString(),
      }, { merge: true });
      setBlockoutRequestStatus("pending");
      setUnsaved(false);
      showToast("Block out request submitted for admin approval", "success");
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setSaving(false);
  }

  // Defaults to rooms x 20, but an admin can raise this per company (e.g. Mercuria
  // needs a higher cap than its room count formula would give it) via the
  // "Preferred Candidate Cap" field on the company's profile in AdminPortal.
  const maxPreferred = parseInt(profile.maxPreferred) || (parseInt(profile.interviewRooms || 1) * 20);

  function toggleStudent(student) {
    if (submitted && !editMode) return;
    if (isLockedOut) return;
    const name = `${student.firstName} ${student.lastName}`;
    setPreferences(prev => {
      const exists = prev.find(p => p.id === student.id);
      if (exists) return prev.filter(p => p.id !== student.id);
      if (prev.length >= maxPreferred) { showToast(`Maximum ${maxPreferred} candidates for your account`, "warning"); return prev; }
      return [...prev, { id: student.id, name, rank: prev.length + 1 }];
    });
    setUnsaved(true);
  }

  async function toggleBlockStudent(id, name) {
    const isBlocked = blockedStudents.some(b => b.id === id);
    const nextBlocked = isBlocked ? blockedStudents.filter(b => b.id !== id) : [...blockedStudents, { id, name }];
    const nextPreferences = isBlocked ? preferences : preferences.filter(p => p.id !== id);
    setBlockedStudents(nextBlocked);
    setPreferences(nextPreferences);
    try {
      await setDoc(doc(db, "boardPreferences", user.uid), { blockedStudents: nextBlocked, preferences: nextPreferences }, { merge: true });
      showToast(isBlocked ? `${name} unblocked` : `${name} blocked — they won't appear in your candidate list or be scheduled with you`, "success");
    } catch (err) { showToast("Error: " + err.message, "error"); }
  }

  async function handleSubmit() {
    if (!window.confirm(`Submit your ${preferences.length} candidate preferences? You can still edit until the deadline.`)) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "boardPreferences", user.uid), {
        profile, additionalAttendees, preferences,
        submitted: true, submittedAt: new Date().toISOString(), submittedBy: user.email
      }, { merge: true });
      setSubmitted(true);
      setEditMode(false);
      setUnsaved(false);
      showToast("Preferences submitted!", "success");
      fireConfetti();
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setSaving(false);
  }

  function addAttendee() { setAdditionalAttendees(p => [...p, { ...BLANK_ATTENDEE }]); setUnsaved(true); }
  function removeAttendee(idx) {
    if (!window.confirm("Remove this attendee?")) return;
    setAdditionalAttendees(p => p.filter((_, i) => i !== idx));
    setUnsaved(true);
  }
  function updateAttendee(idx, field, val) { setAdditionalAttendees(p => p.map((a, i) => i === idx ? { ...a, [field]: val } : a)); setUnsaved(true); }

  const memberTypeLower = profile.memberType?.toLowerCase() || "";
  const isLockedOut = memberTypeLower.includes("platinum") ? platinumPrefsLocked
    : memberTypeLower.includes("gold") ? goldPrefsLocked
    : memberTypeLower.includes("silver") ? silverPrefsLocked
    : false;
  const profileComplete = !!(profile.companyName && profile.mainContactFirst && profile.email && profile.memberType);
  const rsvpComplete = Object.values(profile).some(v => v === true);
  const prefsComplete = submitted;
  const profileCompletion = Math.round(([profile.mainContactFirst, profile.mainContactLast, profile.email, profile.phone, profile.totalAttendees].filter(Boolean).length / 5) * 100);
  const rsvpCount = boardWeekDays.flatMap(d => (d.events || []).filter(e => e.enabled).map(e => e.id)).filter(k => !!profile[k]).length;


  // Load drafted students to exclude from preferences
  const [draftedStudents, setDraftedStudents] = React.useState(new Set());

  React.useEffect(() => {
    if (!profile.memberType) return;
    const memberType = profile.memberType.toLowerCase();
    // Platinum is the first round, so nobody's drafted yet from its
    // perspective. Gold/Silver interview together and draft sequentially
    // afterward, so a student only drops off their lists once they've
    // actually been picked as many times as they need (Group 17 needs 2
    // internships; everyone else needs 1) — not after just one pick.
    if (!(memberType.includes("gold") || memberType.includes("silver"))) { setDraftedStudents(new Set()); return; }
    getDoc(doc(db, "metadata", "draftedStudents")).then(snap => {
      if (!snap.exists()) return;
      const drafted = snap.data().drafted || {};
      const pickCounts = {};
      Object.values(drafted).forEach(names => (names || []).forEach(n => { pickCounts[n] = (pickCounts[n] || 0) + 1; }));
      const requiredPicksFor = name => {
        const s = students.find(st => `${st.firstName} ${st.lastName}` === name);
        return s && s.group === "17" ? 2 : 1;
      };
      const excluded = new Set(Object.keys(pickCounts).filter(name => pickCounts[name] >= requiredPicksFor(name)));
      setDraftedStudents(excluded);
    }).catch(() => {});
  }, [profile.memberType, students]);

  const nonInterviewGroups = new Set(
    (settings.groups || []).filter(g => !g.presentationType).map(g => g.value)
  );
  const interviewGroups = (settings.groups || []).filter(g => g.presentationType);
  const blockedStudentIds = new Set(blockedStudents.map(b => b.id));
  const filtered = students.filter(s => {
    if (nonInterviewGroups.has(s.group)) return false;
    if (candidateGroupFilter !== "all" && s.group !== candidateGroupFilter) return false;
    if (blockedStudentIds.has(s.id)) return false;
    const fullName = `${s.firstName} ${s.lastName}`;
    if (draftedStudents.has(fullName)) return false;
    return fullName.toLowerCase().includes(search.toLowerCase());
  });
  if (candidateSort === "alpha") {
    filtered.sort((a, b) => `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`));
  }

  const inp = (id, focused, setFocused) => ({
    width: "100%", padding: "11px 14px",
    border: `1.5px solid ${focused === id ? MAROON : "#ebebeb"}`,
    borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box",
    background: "white", transition: "all 0.2s",
    boxShadow: focused === id ? "0 0 0 3px rgba(111,0,0,0.07)" : "none",
    color: "#1a1a1a",
  });

  const card = { background: "white", borderRadius: 14, padding: "24px 28px", marginBottom: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)" };
  const btn = { background: `linear-gradient(135deg, ${MAROON}, #a00000)`, color: "white", border: "none", padding: "12px 26px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(111,0,0,0.25)", transition: "all 0.2s", display: "inline-flex", alignItems: "center", gap: 8 };
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, marginBottom: 7, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 };

  const [ferpaConsent, setFerpaConsent] = useState("");
  const ferpaSignedBoard = !!(boardFerpaSignedURL || ferpaConsent === "signed");
  const steps = [
    { id: "profile", label: "Company Profile", done: profileComplete },
    { id: "ferpa", label: "FERPA Agreement", done: ferpaSignedBoard },
    { id: "rsvp", label: "Event RSVP", done: rsvpComplete },
    { id: "candidates", label: "Candidate Preferences", done: prefsComplete },
    ...(profile.memberType ? [{ id: "blockout", label: "Block Out Times (Optional)", done: blockoutRequestStatus === "approved" }] : []),
    ...(schedulePublished ? [{ id: "schedule", label: "Interview Schedule", done: mySchedule.length > 0 }] : []),
    ...(presentationsPublished ? [{ id: "presentations", label: "Presentation Schedule", done: presentationSchedule.length > 0 }] : []),
  ];
  const [focused, setFocused] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [expandedStatements, setExpandedStatements] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [scheduleViewed, setScheduleViewed] = useState(false);
  const { countdown: sessionCountdown } = useSessionTimeout(() => { signOut(auth); navigate("/august"); });

  // Ctrl+S / Cmd+S save shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (activeTab === "rsvp") saveRsvp();
        else if (activeTab === "profile") saveProfile(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "schedule" && mySchedule.length > 0 && !scheduleViewed && user) {
      setScheduleViewed(true);
      setDoc(doc(db, "boardPreferences", user.uid), { scheduleViewedAt: new Date().toISOString() }, { merge: true }).catch(() => {});
    }
  }, [activeTab, mySchedule.length]); // eslint-disable-line

  function exportICal() {
    const yr = settings.programYear || "2025";
    // item.day is the real board-week day label (e.g. "Monday, August 18") written by the scheduler.
    const parse = s => { const m = s && s.match(/(\w+ \d+)\s*$/); return m ? new Date(`${m[1]} ${yr}`) : new Date(`August 18 ${yr}`); };
    const toICS = (d, h, mn) => { const x = new Date(d); x.setHours(h, mn, 0); return x.toISOString().replace(/[-:]/g,"").slice(0,15)+"Z"; };
    const parseT = t => { const m = String(t).match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return {h:9,mn:0}; let h=parseInt(m[1]); const mn=parseInt(m[2]); if(m[3].toUpperCase()==="PM"&&h!==12)h+=12; if(m[3].toUpperCase()==="AM"&&h===12)h=0; return {h,mn}; };
    const events = mySchedule.map((item,i) => {
      const d = parse(item.day);
      const {h,mn} = parseT(item.time);
      return ["BEGIN:VEVENT",`UID:trip-b-${Date.now()}-${i}@tamutrip`,`DTSTART:${toICS(d,h,mn)}`,`DTEND:${toICS(d,h,mn+30)}`,`SUMMARY:Interview: ${item.student}`,`DESCRIPTION:Room ${String(item.room).replace("Room ","")}`,`LOCATION:Koldus Building - Room ${String(item.room).replace("Room ","")}`, "END:VEVENT"].join("\r\n");
    });
    const ical = ["BEGIN:VCALENDAR","VERSION:2.0","CALSCALE:GREGORIAN","PRODID:-//TRIP//EN",...events,"END:VCALENDAR"].join("\r\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([ical],{type:"text/calendar;charset=utf-8"})); a.download="trip-board-schedule.ics"; a.click(); URL.revokeObjectURL(a.href);
  }

  function downloadSchedulePdf() {
    const company = profile.companyName || "Company";
    const preferredNames = new Set(preferences.map(p => p.name));
    const parseTime = t => { const m = String(t).match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return 0; let h=parseInt(m[1]); const mn=parseInt(m[2]); if(m[3].toUpperCase()==="PM"&&h!==12)h+=12; if(m[3].toUpperCase()==="AM"&&h===12)h=0; return h*60+mn; };
    // item.day is the real board-week day label written by the scheduler — order by configured day order.
    const configuredLabels = (settings.boardWeek?.days || []).map(d => d.label);
    const distinctDays = [...new Set(mySchedule.map(i => i.day))];
    const days = distinctDays.sort((a, b) => configuredLabels.indexOf(a) - configuredLabels.indexOf(b));
    const dayLabel = dk => dk;
    const html = `<html><head><title>TRIP Interview Schedule — ${company}</title><style>
      body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a;font-size:13px}
      h1{color:#6f0000;font-size:22px;margin:0 0 4px}
      .sub{color:#666;font-size:13px;margin:0 0 6px}
      .meta{color:#999;font-size:11px;margin:0 0 24px}
      h2{font-size:14px;color:#6f0000;margin:24px 0 10px;border-bottom:2px solid #6f0000;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#6f0000;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px}
      td{padding:9px 12px;border-bottom:1px solid #f0f0f0;vertical-align:top}
      .preferred{color:#1d4ed8;font-weight:700}
      .badge-p{background:#eff6ff;border:1px solid #93c5fd;color:#1d4ed8;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:700}
      .badge-r{background:#f1f5f9;border:1px solid #e2e8f0;color:#64748b;padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600}
      @media print{body{margin:16px}h2{page-break-before:auto}}
    </style></head><body>
      <h1>TRIP Interview Schedule</h1>
      <p class="sub">${company}</p>
      <p class="meta">Generated ${new Date().toLocaleString()}</p>
      ${days.map(day => {
        const items = mySchedule.filter(i => i.day === day).sort((a,b) => parseTime(a.time)-parseTime(b.time));
        return `<h2>${dayLabel(day)}</h2>
          <table><thead><tr><th>Time</th><th>Room</th><th>Student</th><th>Type</th></tr></thead><tbody>
          ${items.map(item => {
            const pref = preferredNames.has(item.student);
            return `<tr><td><b>${fmt12(item.time)}</b></td><td>Room ${String(item.room).replace("Room ","")}</td><td class="${pref?"preferred":""}">${pref?"★ ":""}${item.student||""}</td><td>${pref?'<span class="badge-p">★ PREFERRED</span>':'<span class="badge-r">RANDOM</span>'}</td></tr>`;
          }).join("")}
          </tbody></table>`;
      }).join("")}
    </body></html>`;
    const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.focus(); w.print();
  }

  if (websiteLocked) return (
    <div style={{ minHeight:"100vh",background:"#0a0a0a",fontFamily:"Inter,-apple-system,sans-serif",padding:"48px 24px" }}>
      <div style={{ textAlign:"center",maxWidth:460,margin:"0 auto 40px" }}>
        <div style={{ width:64,height:64,borderRadius:"50%",background:"linear-gradient(135deg,#6f0000,#a00000)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 24px",boxShadow:"0 8px 32px rgba(111,0,0,0.4)" }}>🔒</div>
        <div style={{ fontSize:28,fontWeight:900,color:"white",letterSpacing:-1,marginBottom:12 }}>Website Offline</div>
        <div style={{ fontSize:15,color:"rgba(255,255,255,0.45)",lineHeight:1.7 }}>The TRIP platform is currently offline. Please check back later or contact your program administrator. You can still access resume books and download your interview schedule below.</div>
      </div>
      <div style={{ maxWidth:460,margin:"0 auto",display:"flex",flexDirection:"column",gap:16 }}>
        {resumeBooksPublished && resumeBooks.length > 0 && (
          <div style={{ background:"#141414",border:"1px solid #262626",borderRadius:14,padding:"20px 22px" }}>
            <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Resume Books</div>
            <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
              {resumeBooks.map(book => (
                <a key={book.group} href={book.url + "?t=" + Date.now()} target="_blank" rel="noreferrer"
                  style={{ display:"flex",alignItems:"center",gap:10,background:"#1c1c1c",border:"1.5px solid #2e2e2e",borderRadius:10,padding:"12px 18px",textDecoration:"none",color:"#f0f0f0" }}>
                  <div style={{ width:36,height:36,background:"linear-gradient(135deg, #6f0000, #a00000)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:13,flexShrink:0 }}>G{book.group}</div>
                  <div>
                    <div style={{ fontWeight:700,fontSize:13 }}>Group {book.group} Resume Book</div>
                    <div style={{ fontSize:11,color:"#9ca3af",marginTop:2 }}>Click to open PDF →</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
        <div style={{ background:"#141414",border:"1px solid #262626",borderRadius:14,padding:"20px 22px" }}>
          <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Interview Schedule</div>
          {mySchedule.length > 0 ? (
            <button onClick={downloadSchedulePdf} style={{ background:MAROON,color:"white",border:"none",padding:"11px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer" }}>
              ↓ Download PDF
            </button>
          ) : (
            <div style={{ fontSize:13,color:"#9ca3af" }}>No schedule published yet.</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F3F4F6", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        .skeleton { background:linear-gradient(90deg,#f0f2f5 25%,#e4e7ed 50%,#f0f2f5 75%); background-size:200% 100%; animation:shimmer 1.5s ease-in-out infinite; border-radius:10px; }
        .lift { transition: transform 0.18s, box-shadow 0.18s; }
        .lift:hover { transform: translateY(-2px) !important; box-shadow: 0 10px 32px rgba(0,0,0,0.1) !important; }
        .btn-press { transition: transform 0.12s, opacity 0.12s; }
        .btn-press:active { transform: scale(0.97); opacity: 0.88; }
        .row-hover:hover { background: #f5f7ff !important; }
        .row-hover { transition: background 0.12s; }
        input:focus, textarea:focus, select:focus { outline:none; border-color:#6f0000 !important; box-shadow:0 0 0 3px rgba(111,0,0,0.1) !important; }
        .mobile-bp-toggle { display:none !important; }
        .sidebar-overlay-bp { display:none; }
        @media (max-width:768px) {
          .bp-sidebar { transform:translateX(-240px); transition:transform 0.28s cubic-bezier(0.4,0,0.2,1) !important; }
          .bp-sidebar.open { transform:translateX(0) !important; }
          .bp-main { margin-left:0 !important; padding:20px 18px !important; }
          .mobile-bp-toggle { display:flex !important; }
          .sidebar-overlay-bp.open { display:block !important; }
        }
      `}</style>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
      <div className={`sidebar-overlay-bp${sidebarOpen?" open":""}`} onClick={() => setSidebarOpen(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:99,backdropFilter:"blur(2px)" }} />
      <button className="mobile-bp-toggle" onClick={() => setSidebarOpen(o=>!o)} style={{ position:"fixed",top:14,left:14,zIndex:200,background:MAROON,border:"none",borderRadius:8,width:38,height:38,cursor:"pointer",color:"white",fontSize:18,alignItems:"center",justifyContent:"center" }}>☰</button>

      {/* Student profile modal */}
      {previewStudent && (
        <Modal onClose={() => setPreviewStudent(null)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {previewStudent.photoURL ? (
                <img src={previewStudent.photoURL} alt="" style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", border: `3px solid ${MAROON}` }} />
              ) : (
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>◎</div>
              )}
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#0f0f0f" }}>{previewStudent.firstName} {previewStudent.lastName}</div>
                <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>{previewStudent.undergradDegree} · Group {previewStudent.group} · {previewStudent.classYear}</div>
              </div>
            </div>
            <button onClick={() => setPreviewStudent(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa", padding: "4px 8px" }}>×</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              ["Email", previewStudent.email],
              ["Hometown", previewStudent.hometown],
              ["Expected Grad", previewStudent.expectedGraduation],
              ["Seeking MFM", previewStudent.seekingMFM],
            ].filter(([,v]) => v).map(([label, value]) => (
              <div key={label} style={{ background: "#f8f8f8", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{value}</div>
              </div>
            ))}
          </div>
          {previewStudent.personalStatement && (
            <div style={{ background: "#fafafa", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Personal Statement</div>
              <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>{previewStudent.personalStatement}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            {previewStudent.resumeURL && (
              <a href={previewStudent.resumeURL} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: "none", padding: "10px 20px", fontSize: 13 }}>View Resume →</a>
            )}
            <button onClick={() => { toggleStudent(previewStudent); setPreviewStudent(null); }}
              style={{ background: preferences.find(p => p.id === previewStudent.id) ? "#f0fdf4" : "#fff0f0", border: `2px solid ${preferences.find(p => p.id === previewStudent.id) ? "#86efac" : MAROON}`, color: preferences.find(p => p.id === previewStudent.id) ? "#166534" : MAROON, padding: "10px 20px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {preferences.find(p => p.id === previewStudent.id) ? "✓ Selected" : "+ Select Candidate"}
            </button>
          </div>
        </Modal>
      )}

      {/* Sidebar */}
      <div className={`bp-sidebar${sidebarOpen?" open":""}`} style={{ width: 240, background: `linear-gradient(180deg, ${DARK} 0%, #2A0A0A 100%)`, display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100 }}>
        <div style={{ padding: "26px 20px 22px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, background: `linear-gradient(135deg, ${MAROON}, #a00000)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "white", fontSize: 15, boxShadow: "0 4px 10px rgba(111,0,0,0.4)" }}>T</div>
            <div>
              <div style={{ color: "white", fontWeight: 800, fontSize: 15 }}>TRIP</div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>Board Member</div>
            </div>
          </div>
          {boardWeekStart && (
            <div style={{ marginTop: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Board Week Starts In</div>
              <Countdown target={boardWeekStart} doneLabel="Board week is here!" />
            </div>
          )}
        </div>
        <div style={{ padding: "18px 14px", flex: 1 }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 1.5, textTransform: "uppercase", padding: "0 8px", marginBottom: 10 }}>Progress</div>
          {steps.map((step, i) => (
            <div key={step.id} onClick={() => switchTab(step.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: 8, marginBottom: 4, cursor: "pointer", background: activeTab === step.id ? "rgba(255,255,255,0.1)" : "transparent", borderLeft: activeTab === step.id ? `2px solid ${MAROON}` : "2px solid transparent", transition: "all 0.15s" }}
              onMouseOver={e => { if (activeTab !== step.id) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
              onMouseOut={e => { if (activeTab !== step.id) e.currentTarget.style.background = "transparent"; }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: step.done ? "#22c55e" : activeTab === step.id ? MAROON : "rgba(255,255,255,0.1)", border: `2px solid ${step.done ? "#22c55e" : activeTab === step.id ? MAROON : "rgba(255,255,255,0.15)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white", fontWeight: 800, flexShrink: 0, transition: "all 0.2s" }}>
                {step.done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 13, fontWeight: activeTab === step.id ? 600 : 400, color: step.done ? "#86efac" : activeTab === step.id ? "white" : "rgba(255,255,255,0.45)" }}>{step.label}</span>
            </div>
          ))}
          <div style={{ marginTop: 20, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: submitted ? "#22c55e" : "#f59e0b", boxShadow: `0 0 8px ${submitted ? "#22c55e" : "#f59e0b"}` }} />
              <span style={{ fontSize: 12, color: submitted ? "#86efac" : "#fde68a", fontWeight: 600 }}>{submitted ? "Submitted" : "Not yet submitted"}</span>
            </div>
            {submitted && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 6 }}>You can still edit until deadline</div>}
          </div>
        </div>
        <div style={{ padding: "14px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div onClick={() => { if (unsaved && !window.confirm("Leave without saving?")) return; signOut(auth); navigate("/august"); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, cursor: "pointer", color: "rgba(255,255,255,0.3)", fontSize: 13, transition: "all 0.15s" }}
            onMouseOver={e => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            onMouseOut={e => { e.currentTarget.style.color = "rgba(255,255,255,0.3)"; e.currentTarget.style.background = "transparent"; }}>
            → Sign Out
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="bp-main" style={{ marginLeft: 240, flex: 1, padding: "32px 40px", position: "relative", overflowY: "auto" }}>
        {isLockedOut && activeTab !== "schedule" && (
          <div style={{ position: "sticky", top: 0, zIndex: 50, background: "#fff1f0", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 600, color: "#991b1b" }}>
            🔒 Preferences are locked. Contact admin to reopen.
          </div>
        )}
        {/* Excludes "candidates" too — every mutating control on that tab (select/drag/block/
            submit) already checks isLockedOut itself (toggleStudent, canInteract, canDrag), so
            this catch-all isn't needed there, and skipping it lets the candidate list and
            rankings' own scroll containers receive wheel events again instead of the page
            behind them (a covering div always wins hit-testing over what's visually beneath it). */}
        {isLockedOut && activeTab !== "schedule" && activeTab !== "candidates" && <div style={{ position: "absolute", inset: 0, zIndex: 40, cursor: "not-allowed" }} onClick={() => showToast("Preferences are locked — contact admin to reopen", "error")} />}

        {/* Rendered as a direct sibling of the lock overlay above (not nested inside a
            tab's fadeUp-animated wrapper) — a running opacity/transform animation creates
            its own stacking context, which would trap a nested z-index and stop it from
            ever out-ranking the overlay's zIndex:40 regardless of what value it's given. */}
        {activeTab === "candidates" && resumeBooksPublished && resumeBooks.length > 0 && (
          <div style={{ position: "relative", zIndex: 41, background: "white", borderRadius: 14, padding: "22px 24px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14 }}>Resume Books</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {resumeBooks.map(book => (
                <a key={book.group} href={book.url + "?t=" + Date.now()} target="_blank" rel="noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8f8f8", border: "1.5px solid #e8e8e8", borderRadius: 10, padding: "12px 18px", textDecoration: "none", transition: "all 0.2s", color: "#333" }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = MAROON; e.currentTarget.style.background = "#fff0f0"; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.background = "#f8f8f8"; }}>
                  <div style={{ width: 36, height: 36, background: `linear-gradient(135deg, ${MAROON}, #a00000)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>G{book.group}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a" }}>Group {book.group} Resume Book</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Click to open PDF →</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div className="skeleton" style={{ height:70,marginBottom:22 }} />
            <div className="skeleton" style={{ height:200,marginBottom:18 }} />
            <div className="skeleton" style={{ height:140 }} />
          </div>
        )}
        {!loading && <>
        {/* Progress stepper */}
        <div style={{ ...card, padding: "18px 26px", marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {steps.map((step, i) => (
              <React.Fragment key={step.id}>
                <div onClick={() => switchTab(step.id)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "3px 6px", borderRadius: 8, transition: "background 0.15s" }}
                  onMouseOver={e => e.currentTarget.style.background = "#f8f8f8"}
                  onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: step.done ? "#22c55e" : activeTab === step.id ? MAROON : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: step.done || activeTab === step.id ? "white" : "#aaa", fontWeight: 800, transition: "all 0.2s", flexShrink: 0, boxShadow: activeTab === step.id ? `0 0 14px rgba(111,0,0,0.25)` : "none" }}>
                    {step.done ? "✓" : i + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: step.done ? "#166534" : activeTab === step.id ? "#1a1a1a" : "#9ca3af" }}>{step.label}</div>
                    <div style={{ fontSize: 11, color: step.done ? "#22c55e" : activeTab === step.id ? MAROON : "#d1d5db" }}>{step.done ? "Completed" : activeTab === step.id ? "In progress" : "Not started"}</div>
                  </div>
                </div>
                {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: step.done ? "linear-gradient(90deg,#22c55e,#86efac)" : "#f0f0f0", margin: "0 12px", borderRadius: 2, transition: "background 0.4s" }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* PROFILE */}
        {activeTab === "profile" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 22 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Company Profile</h1>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Tell us about your company and primary contact</p>
            </div>

            {boardAnnouncements.length > 0 && <div style={{ marginBottom:20 }}>{boardAnnouncements.map(ann=>(<div key={ann.id} style={{ background:"linear-gradient(135deg,#fff8f0,#fff3e0)",border:"1px solid #fbbf24",borderRadius:12,padding:"14px 18px",marginBottom:10,position:"relative" }}><button onClick={()=>dismissAnnouncement(ann.id)} style={{ position:"absolute",top:10,right:10,background:"none",border:"none",color:"#92400e",opacity:0.6,fontSize:16,cursor:"pointer",lineHeight:1,padding:4 }} aria-label="Dismiss">×</button><div style={{ fontWeight:700,fontSize:14,color:"#92400e",marginBottom:4,paddingRight:20 }}>📢 {ann.title}</div><div style={{ fontSize:13,color:"#78350f",lineHeight:1.6,paddingRight:20 }}>{ann.body}</div></div>))}</div>}

            {/* Summary card — shows when profile has data */}
            {profile.companyName && (
              <div style={{ ...card, background: "linear-gradient(135deg, #fff8f8, #fff0f0)", border: `1px solid rgba(111,0,0,0.12)`, padding: "18px 24px", marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: MAROON, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Profile Summary</div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {[
                    ["Company", profile.companyName],
                    ["Type", profile.memberType?.replace(" Board Member","") || "—"],
                    ["Rooms", profile.interviewRooms],
                    ["Attendees", profile.totalAttendees || "—"],
                    ["Contact", `${profile.mainContactFirst} ${profile.mainContactLast}`.trim() || "—"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>{value}</div>
                    </div>
                  ))}
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                    <div style={{ fontSize: 12, color: profileCompletion === 100 ? "#166534" : "#92400e", fontWeight: 700, background: profileCompletion === 100 ? "#f0fdf4" : "#fffbeb", border: `1px solid ${profileCompletion === 100 ? "#86efac" : "#fcd34d"}`, padding: "4px 12px", borderRadius: 20 }}>
                      {profileCompletion}% complete
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!ferpaSignedBoard && (
              <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
                <div style={{ fontSize:13,color:"#92400e",fontWeight:600 }}>FERPA agreement required — please complete the FERPA Agreement step</div>
                <button type="button" onClick={()=>setActiveTab("ferpa")} style={{ background:"#6f0000",color:"white",border:"none",padding:"8px 16px",borderRadius:8,fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0 }}>Go to FERPA →</button>
              </div>
            )}
            {ferpaSignedBoard && (
              <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:12,padding:"14px 18px",fontSize:13,color:"#166534",fontWeight:600 }}>
                FERPA & Confidentiality Agreement signed electronically
              </div>
            )}

            <form onSubmit={saveProfile}>
              {/* Member type */}
              <div style={card}>
                <div style={lbl}>Board Member Type *</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  {[
                    { value: "Platinum Board Member", label: "Platinum", desc: tierInterviewDaysLabel("platinum"), color: TIER_COLORS.platinum },
                    { value: "Gold Board Member", label: "Gold", desc: tierInterviewDaysLabel("goldsilver"), color: TIER_COLORS.gold },
                    { value: "Silver Board Member", label: "Silver", desc: tierInterviewDaysLabel("goldsilver"), color: TIER_COLORS.silver },
                  ].map(type => (
                    <div key={type.value} onClick={() => handleMemberTypeSelect(type.value)}
                      style={{ padding: "16px 14px", borderRadius: 12, border: `2px solid ${profile.memberType === type.value ? type.color : "#ebebeb"}`, background: profile.memberType === type.value ? `${type.color}0d` : "white", cursor: "pointer", textAlign: "center", transition: "all 0.2s" }}
                      onMouseOver={e => { if (profile.memberType !== type.value) e.currentTarget.style.borderColor = `${type.color}55`; }}
                      onMouseOut={e => { if (profile.memberType !== type.value) e.currentTarget.style.borderColor = "#ebebeb"; }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: profile.memberType === type.value ? type.color : "#555", marginBottom: 4 }}>{type.label}</div>
                      <div style={{ fontSize: 11, color: "#aaa" }}>{type.desc}</div>
                      {profile.memberType === type.value && <div style={{ width: 6, height: 6, borderRadius: "50%", background: type.color, margin: "8px auto 0", boxShadow: `0 0 8px ${type.color}` }} />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Company info */}
              <div style={card}>
                <div style={lbl}>Company & Contact Information</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lbl}>Company Name</label>
                    <input value={profile.companyName} readOnly style={{ ...inp("cn", focused, setFocused), background: "#f5f5f5", color: "#555", cursor: "not-allowed" }} />
                  </div>
                  {[["First Name","mainContactFirst","fn",true],["Last Name","mainContactLast","ln",false],["Email","email","em",true],["Phone","phone","ph",false]].map(([label, key, id, isRequired]) => (
                    <div key={key}>
                      <label style={lbl}>{label}{isRequired ? " *" : ""}</label>
                      <input value={profile[key]} onChange={e => updateProfile(key, e.target.value)} type={key === "email" ? "email" : "text"} required={isRequired} style={inp(id, focused, setFocused)} onFocus={() => setFocused(id)} onBlur={() => setFocused(null)} />
                    </div>
                  ))}
                  <div>
                    <label style={lbl}>Interview Rooms</label>
                    <select value={profile.interviewRooms} onChange={e => updateProfile("interviewRooms", e.target.value)} style={inp("ir", focused, setFocused)} onFocus={() => setFocused("ir")} onBlur={() => setFocused(null)}>
                      {[1,2].map(n => <option key={n} value={n}>{n} Room{n > 1 ? "s" : ""}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Total Attendees</label>
                    <input type="number" value={profile.totalAttendees} onChange={e => updateProfile("totalAttendees", e.target.value)} placeholder="e.g. 3" style={inp("ta", focused, setFocused)} onFocus={() => setFocused("ta")} onBlur={() => setFocused(null)} />
                  </div>
                  <div>
                    <label style={lbl}>Number of Spring Internships Offered</label>
                    <input type="number" min="0" value={profile.springInternshipsOffered} onChange={e => updateProfile("springInternshipsOffered", e.target.value)} placeholder="e.g. 3" style={inp("iosp", focused, setFocused)} onFocus={() => setFocused("iosp")} onBlur={() => setFocused(null)} />
                  </div>
                  <div>
                    <label style={lbl}>Number of Summer Internships Offered</label>
                    <input type="number" min="0" value={profile.summerInternshipsOffered} onChange={e => updateProfile("summerInternshipsOffered", e.target.value)} placeholder="e.g. 2" style={inp("iosu", focused, setFocused)} onFocus={() => setFocused("iosu")} onBlur={() => setFocused(null)} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lbl}>Company Address</label>
                    <input value={profile.address} onChange={e => updateProfile("address", e.target.value)} placeholder="Street, City, State, ZIP" style={inp("addr", focused, setFocused)} onFocus={() => setFocused("addr")} onBlur={() => setFocused(null)} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lbl}>Dietary Restrictions</label>
                    <input value={profile.dietaryRestrictions} onChange={e => updateProfile("dietaryRestrictions", e.target.value)} placeholder="Tree nuts, kosher, no pork, etc." style={inp("dr", focused, setFocused)} onFocus={() => setFocused("dr")} onBlur={() => setFocused(null)} />
                  </div>
                </div>
              </div>

              {/* Additional attendees */}
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div style={lbl}>Additional Attendees</div>
                  <button type="button" onClick={addAttendee} style={{ background: "white", border: `1.5px solid ${MAROON}`, color: MAROON, padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>+ Add</button>
                </div>
                {additionalAttendees.map((attendee, idx) => (
                  <div key={idx} style={{ border: "1px solid #f0f0f0", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 10 }}>
                      <div>
                        <label style={{ ...lbl, fontSize: 10 }}>Full Name</label>
                        <input value={attendee.name} onChange={e => updateAttendee(idx, "name", e.target.value)} placeholder="Jane Smith" style={inp(`an${idx}`, focused, setFocused)} onFocus={() => setFocused(`an${idx}`)} onBlur={() => setFocused(null)} />
                      </div>
                      <div>
                        <label style={{ ...lbl, fontSize: 10 }}>Email</label>
                        <input type="email" value={attendee.email} onChange={e => updateAttendee(idx, "email", e.target.value)} placeholder="jane@company.com" style={inp(`ae${idx}`, focused, setFocused)} onFocus={() => setFocused(`ae${idx}`)} onBlur={() => setFocused(null)} />
                      </div>
                      <button type="button" onClick={() => removeAttendee(idx)} style={{ background: "none", border: "1.5px solid #fca5a5", color: "#dc2626", padding: "10px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", marginBottom: 1 }}>×</button>
                    </div>
                    <div>
                      <label style={{ ...lbl, fontSize: 10 }}>Dietary Restrictions</label>
                      <input value={attendee.dietaryRestrictions || ""} onChange={e => updateAttendee(idx, "dietaryRestrictions", e.target.value)} placeholder="Tree nuts, kosher, no pork, etc." style={inp(`ad${idx}`, focused, setFocused)} onFocus={() => setFocused(`ad${idx}`)} onBlur={() => setFocused(null)} />
                    </div>
                  </div>
                ))}
                {additionalAttendees.length === 0 && <div style={{ color: "#d1d5db", fontSize: 13, textAlign: "center", padding: "12px 0" }}>No additional attendees</div>}
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="submit" disabled={saving} style={btn}
                  onMouseOver={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(111,0,0,0.35)"; }}
                  onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(111,0,0,0.25)"; }}>
                  {saving ? <><span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />Saving...</> : "Save Profile"}
                </button>
                <button type="button" onClick={async () => { await saveProfile(null); switchTab("rsvp"); }} style={{ background: "white", border: "1.5px solid #e0e0e0", color: "#555", padding: "12px 22px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  Save & Continue: Event RSVP →
                </button>
              </div>
            </form>
          </div>
        )}

        {/* FERPA */}
        {activeTab === "ferpa" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 20 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>FERPA & Confidentiality Agreement</h1>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Read the full agreement below, then complete the electronic signature at the bottom.</p>
            </div>
            {ferpaSignedBoard ? (
              <div style={{ background:"white",borderRadius:14,padding:"28px 32px",boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:"16px 20px",marginBottom:16,fontSize:14,color:"#166534",fontWeight:700 }}>
                  Agreement signed electronically
                </div>
                <div style={{ fontSize:13,color:"#555",marginBottom:6 }}>Name: <strong>{profile.mainContactFirst} {profile.mainContactLast}</strong></div>
                <div style={{ fontSize:13,color:"#555",marginBottom:6 }}>Company: <strong>{profile.companyName}</strong></div>
                <div style={{ fontSize:13,color:"#555" }}>Email: <strong>{profile.email || user?.email}</strong></div>
                <button type="button" onClick={async()=>{ await setDoc(doc(db,"boardPreferences",user.uid),{ferpaConsent:"",ferpaConsentStatus:"",ferpaSignedAt:""},{merge:true}); setFerpaConsent(""); setBoardFerpaSignedURL(""); setBoardFerpaChecked(false); }} style={{ marginTop:16,background:"none",border:"1px solid #e8e8e8",color:"#888",padding:"8px 16px",borderRadius:8,fontSize:12,cursor:"pointer" }}>Revoke &amp; Re-sign</button>
              </div>
            ) : (
              <>
                {/* Agreement text */}
                <div style={{ background:"white",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,0.05)",marginBottom:16 }}>
                  <div style={{ background:"linear-gradient(135deg,#6f0000,#a00000)",padding:"16px 24px" }}>
                    <div style={{ color:"white",fontWeight:800,fontSize:14,letterSpacing:0.2 }}>TRIP INDUSTRY ADVISORY BOARD FERPA AND CONFIDENTIALITY ACKNOWLEDGMENT</div>
                  </div>
                  <div style={{ padding:"22px 24px",maxHeight:420,overflowY:"auto",fontSize:13,color:"#374151",lineHeight:1.8 }}>
                    <p style={{ marginBottom:16 }}>I serve as a member, representative, or designee of the Industry Advisory Board for the Trading, Risk and Investments Program ("TRIP") at Texas A&amp;M University.</p>
                    <p style={{ marginBottom:16 }}>In that capacity, I may be given access to student materials and information through a TRIP website, online platform, email, shared folder, or other means. These materials may include resumes, transcripts, grade information, GPA, academic history, enrollment information, contact information, professional profiles, internship materials, and other information provided by students for TRIP-related recruiting, internship, mentoring, advisory-board, and professional-development purposes.</p>
                    <p style={{ marginBottom:16 }}>I understand that some of these materials may contain personally identifiable information from student education records protected by the Family Educational Rights and Privacy Act of 1974 ("FERPA").</p>
                    <p style={{ marginBottom:16 }}>By signing this acknowledgment, I agree to the following:</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>1. Authorized purpose</div>
                    <p style={{ marginBottom:8 }}>I will access and use TRIP student materials only for legitimate TRIP-related purposes, including internship or employment recruiting, student mentoring, professional development, advisory-board activities, and evaluation of students for opportunities connected to TRIP or my organization.</p>
                    <p style={{ marginBottom:16 }}>I will not use student materials for any unrelated personal, commercial, marketing, research, training, publication, or other purpose.</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>2. Confidentiality and limited access</div>
                    <p style={{ marginBottom:8 }}>I will treat student materials and information as confidential. I will not share, forward, copy, publish, post, transmit, sell, or otherwise disclose student materials or information to any person or entity except as expressly permitted below.</p>
                    <p style={{ marginBottom:8 }}>I may share student materials within my organization only with officers, employees, agents, recruiters, hiring managers, or other representatives who have a legitimate need to review the materials for the same TRIP-related purpose for which the materials were provided, and only if such persons are informed that the materials are confidential and may not be used or redisclosed for any other purpose.</p>
                    <p style={{ marginBottom:16 }}>I will not disclose student materials to any outside person, company, affiliate, client, vendor, consultant, database, website, social-media platform, artificial-intelligence tool, or other third party unless the student has provided prior written consent or Texas A&amp;M University has confirmed in writing that the disclosure is permitted.</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>3. No redisclosure</div>
                    <p style={{ marginBottom:8 }}>I understand that student materials made available through TRIP may include FERPA-protected personally identifiable information from education records. I agree that such information may not be redisclosed to another party unless the redisclosure is authorized by the student's written consent, permitted by FERPA, and consistent with the purpose for which the information was originally disclosed.</p>
                    <p style={{ marginBottom:16 }}>I understand that access to these materials does not make them public information and does not authorize me or my organization to use or disclose them beyond the limited TRIP-related purpose for which they were provided.</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>4. Safeguarding of student information</div>
                    <p style={{ marginBottom:8 }}>I will use reasonable safeguards to protect student materials from unauthorized access, disclosure, copying, loss, or misuse. These safeguards include, as appropriate, limiting access to individuals with a need to know, storing electronic copies in secure systems, avoiding unnecessary downloads or printouts, and not leaving printed or electronic materials accessible to unauthorized persons.</p>
                    <p style={{ marginBottom:16 }}>I will not upload student transcripts, resumes, or other student materials into publicly accessible systems, external databases, social-media platforms, generative AI tools, or other platforms not approved for handling confidential student information.</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>5. Retention and deletion</div>
                    <p style={{ marginBottom:16 }}>If TRIP notifies me that a student has revoked consent or that access to particular materials should be discontinued, I will stop using those materials prospectively and will reasonably cooperate with any request to delete, destroy, return, or restrict further use of the materials, subject to actions already taken and any lawful record-retention requirements.</p>

                    <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>6. Acknowledgment</div>
                    <p>I understand that my access to TRIP student materials is conditioned on this acknowledgment and on compliance with applicable confidentiality and FERPA-related restrictions.</p>
                  </div>
                </div>

                {/* Signature block */}
                <div style={{ background:"white",borderRadius:14,padding:"24px 28px",boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:18 }}>Electronic Signature</div>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16 }}>
                    {[
                      ["Name",`${profile.mainContactFirst||""} ${profile.mainContactLast||""}`.trim()||"(set in Profile)"],
                      ["Company / Organization",profile.companyName||"(set in Profile)"],
                      ["Title",profile.title||""],
                      ["Email",profile.email||user?.email||""],
                    ].map(([label,val])=>(
                      <div key={label}>
                        <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>{label}</div>
                        <div style={{ background:"#f5f5f5",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#555",border:"1.5px solid #ebebeb" }}>{val||<span style={{ color:"#bbb" }}>—</span>}</div>
                      </div>
                    ))}
                    <div>
                      <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>Date</div>
                      <div style={{ background:"#f5f5f5",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#555",border:"1.5px solid #ebebeb" }}>{new Date().toLocaleDateString()}</div>
                    </div>
                  </div>
                  <label style={{ display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",marginBottom:20,padding:"14px 16px",background:boardFerpaChecked?"#f0fdf4":"#f8f8f8",border:`1.5px solid ${boardFerpaChecked?"#86efac":"#e8e8e8"}`,borderRadius:10,transition:"all 0.2s" }}>
                    <input type="checkbox" checked={boardFerpaChecked} onChange={e=>setBoardFerpaChecked(e.target.checked)} style={{ marginTop:2,width:16,height:16,accentColor:MAROON,flexShrink:0 }} />
                    <span style={{ fontSize:13,color:"#374151",lineHeight:1.6 }}>I have read this acknowledgment, understand it, and agree to the confidentiality obligations and FERPA-related restrictions described above with respect to TRIP student materials.</span>
                  </label>
                  <button type="button" disabled={!boardFerpaChecked||boardFerpaSubmitting} onClick={async()=>{
                    if(!boardFerpaChecked||!user)return;
                    setBoardFerpaSubmitting(true);
                    try{
                      const ts=new Date().toISOString();
                      await setDoc(doc(db,"boardPreferences",user.uid),{ferpaConsent:"signed",ferpaConsentStatus:"signed",ferpaSignedAt:ts,ferpaSignedName:`${profile.mainContactFirst||""} ${profile.mainContactLast||""}`.trim(),ferpaSignedCompany:profile.companyName},{merge:true});
                      setFerpaConsent("signed");
                      setBoardFerpaSignedURL("signed");
                      showToast("FERPA agreement signed — thank you!","success");
                      setActiveTab("rsvp");
                    }catch(err){showToast("Error: "+err.message,"error");}
                    setBoardFerpaSubmitting(false);
                  }} style={{ width:"100%",padding:"13px",background:boardFerpaChecked?"linear-gradient(135deg,#6f0000,#a00000)":"#d1d5db",color:"white",border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:boardFerpaChecked?"pointer":"not-allowed",transition:"all 0.2s" }}>
                    {boardFerpaSubmitting?"Submitting…":"Sign & Submit →"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* RSVP */}
        {activeTab === "rsvp" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 22 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Event RSVP</h1>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Indicate which events your team will attend. Due by {settings.rsvpDeadline || "Thursday, August 4"}.</p>
            </div>


            <div style={{ ...card, padding: "16px 22px", marginBottom: 18, background: rsvpCount > 0 ? "#f0fdf4" : "#fffbeb", border: `1px solid ${rsvpCount > 0 ? "#86efac" : "#fcd34d"}` }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: rsvpCount > 0 ? "#166534" : "#92400e" }}>
                {rsvpCount > 0 ? `Attending ${rsvpCount} event${rsvpCount !== 1 ? "s" : ""}` : "No events selected — please indicate your attendance below"}
              </span>
            </div>

            {/* Schedule-driven event cards — one card per configured day with enabled events */}
            {boardWeekDays.filter(day => (day.events || []).some(e => e.enabled)).map(day => (
              <div key={day.id} style={card}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#0f0f0f", marginBottom: 16 }}>{day.label}</div>
                {(day.events || []).filter(e => e.enabled).map(evt => (
                  <label key={evt.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 6, cursor: "pointer", background: profile[evt.id] ? "#fff0f0" : "#fafafa", border: `1.5px solid ${profile[evt.id] ? "#fca5a5" : "#f0f0f0"}`, transition: "all 0.15s" }}>
                    <input type="checkbox" checked={!!profile[evt.id]} onChange={e => updateProfile(evt.id, e.target.checked)} style={{ accentColor: MAROON, width: 16, height: 16, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: profile[evt.id] ? 700 : 500, color: profile[evt.id] ? MAROON : "#555" }}>{evt.name}</div>
                      {(evt.startTime || evt.endTime) && (
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                          {evt.startTime}{evt.startTime && evt.endTime ? " – " : ""}{evt.endTime}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            ))}

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={saveRsvp} disabled={savingRsvp} style={btn}
                onMouseOver={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
                {savingRsvp ? <><span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />Saving...</> : "Save RSVP"}
              </button>
              <button onClick={async () => { await saveRsvp(); switchTab("candidates"); }} style={{ background: "white", border: "1.5px solid #e0e0e0", color: "#555", padding: "12px 22px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Save & Continue: Candidates →
              </button>
            </div>
          </div>
        )}

        {/* CANDIDATES */}
        {activeTab === "schedule" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Interview Schedule</h1>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>Your confirmed interview schedule for board week</p>
              </div>
              {mySchedule.length > 0 && (
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={downloadSchedulePdf} style={{ background: "white", border: `1.5px solid ${MAROON}`, color: MAROON, padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(111,0,0,0.1)" }}>
                    ↓ Download PDF
                  </button>
                  <button onClick={exportICal} style={{ background: "white", border: "1.5px solid #e0e0e0", color: "#555", padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    📅 Add to Calendar
                  </button>
                </div>
              )}
            </div>
            {mySchedule.length === 0 ? (
              <div style={{ background: "white", borderRadius: 14, padding: "40px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📅</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#333", marginBottom: 8 }}>No interviews scheduled yet</div>
                <div style={{ color: "#9ca3af", fontSize: 14 }}>Your schedule will appear here once the admin publishes it.</div>
              </div>
            ) : (
              <div>
                {(() => {
                  const preferredNames = new Set(preferences.map(p => p.name));
                  const configuredLabels = boardWeekDays.map(d => d.label);
                  const distinctDays = [...new Set(mySchedule.map(i => i.day))];
                  const orderedDays = distinctDays.sort((a, b) => configuredLabels.indexOf(a) - configuredLabels.indexOf(b));
                  return orderedDays.map(day => {
                  const dayItems = mySchedule.filter(i => i.day === day);
                  if (!dayItems.length) return null;
                  const rooms = [...new Set(dayItems.map(i => i.room))].sort((a, b) => {
                    const na = parseInt(String(a).replace(/\D/g, "")) || 0;
                    const nb = parseInt(String(b).replace(/\D/g, "")) || 0;
                    return na - nb;
                  });
                  const parseTime = t => {
                    const s = String(t || "");
                    const ampm = s.match(/(\d+):(\d+)\s*(AM|PM)/i);
                    if (ampm) { let h = parseInt(ampm[1]); const mn = parseInt(ampm[2]); if(ampm[3].toUpperCase()==="PM"&&h!==12)h+=12; if(ampm[3].toUpperCase()==="AM"&&h===12)h=0; return h*60+mn; }
                    const mil = s.match(/^(\d+):(\d+)$/);
                    if (mil) return parseInt(mil[1])*60+parseInt(mil[2]);
                    return 0;
                  };
                  const times = [...new Set(dayItems.map(i => i.time))].sort((a, b) => parseTime(a) - parseTime(b));
                  return (
                    <div key={day} style={{ background: "white", borderRadius: 14, marginBottom: 18, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)" }}>
                      <div style={{ background: `linear-gradient(135deg, ${MAROON}, #a00000)`, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ color: "white", fontWeight: 800, fontSize: 16 }}>{day}</div>
                        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{dayItems.length} interview{dayItems.length !== 1 ? "s" : ""} · {rooms.length} room{rooms.length !== 1 ? "s" : ""}</div>
                      </div>
                      <div style={{ display:"flex",gap:20,padding:"10px 18px",background:"#f8fafc",borderBottom:"1px solid #f0f0f0",fontSize:12 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                          <span style={{ fontSize:13,color:"#1d4ed8" }}>★</span>
                          <span style={{ fontWeight:700,color:"#1d4ed8" }}>Preferred</span>
                          <span style={{ color:"#9ca3af" }}>— on your selection list</span>
                        </div>
                        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                          <span style={{ background:"#f1f5f9",border:"1px solid #e2e8f0",padding:"1px 7px",borderRadius:4,color:"#64748b",fontWeight:600,fontSize:10,letterSpacing:0.3 }}>RANDOM</span>
                          <span style={{ color:"#9ca3af" }}>— system-assigned</span>
                        </div>
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 400 }}>
                          <thead>
                            <tr style={{ background: "#f8f8f8", borderBottom: "2px solid #f0f0f0" }}>
                              <th style={{ textAlign: "left", padding: "11px 18px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", borderRight:"2px solid #f0f0f0" }}>Time</th>
                              {rooms.map(room => (
                                <th key={room} style={{ textAlign: "left", padding: "11px 18px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, borderRight:"1px solid #f5f5f5" }}>
                                  Room {String(room).replace("Room ", "")}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {times.map((time, ti) => (
                              <tr key={time} style={{ borderBottom: "1px solid #f0f0f0", background: ti % 2 === 0 ? "white" : "#fafcff" }}>
                                <td style={{ padding: "14px 18px", fontWeight: 800, color: MAROON, fontSize: 14, whiteSpace: "nowrap", borderRight:"2px solid #f0f0f0" }}>{fmt12(time)}</td>
                                {rooms.map(room => {
                                  const item = dayItems.find(i => i.time === time && i.room === room);
                                  const isPreferred = item ? preferredNames.has(item.student) : false;
                                  return (
                                    <td key={room} style={{ padding: "12px 18px", fontSize: 13, background: isPreferred ? "rgba(29,78,216,0.04)" : undefined, borderRight:"1px solid #f5f5f5" }}>
                                      {item ? (
                                        <div>
                                          <div style={{ fontWeight: 700, color: isPreferred ? "#1d4ed8" : "#1a1a1a", marginBottom: 5, display:"flex", alignItems:"center", gap:5 }}>
                                            {isPreferred && <span style={{ color:"#1d4ed8",fontSize:13 }}>★</span>}
                                            {item.student}
                                          </div>
                                          {isPreferred
                                            ? <span style={{ fontSize:10,fontWeight:700,color:"#1d4ed8",background:"#eff6ff",border:"1px solid #93c5fd",padding:"2px 7px",borderRadius:4,letterSpacing:0.3 }}>★ PREFERRED</span>
                                            : <span style={{ fontSize:10,fontWeight:600,color:"#64748b",background:"#f1f5f9",border:"1px solid #e2e8f0",padding:"2px 7px",borderRadius:4,letterSpacing:0.3 }}>RANDOM</span>
                                          }
                                        </div>
                                      ) : (
                                        <span style={{ color:"#e2e8f0",fontSize:18 }}>·</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                });
              })()}
              </div>
            )}
          </div>
        )}

        {activeTab === "presentations" && presentationsPublished && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 22, display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Presentation Schedule</h1>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>Student presentations during board week — grouped by room</p>
              </div>
              <button onClick={()=>{
                const byRoom = {};
                presentationSchedule.forEach(e=>{ (byRoom[e.room]=byRoom[e.room]||{topic:e.topic,entries:[]}).entries.push(e); });
                const html = `<html><head><title>TRIP Presentation Schedule</title><style>body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a}h1{color:#6f0000;font-size:22px;margin-bottom:4px}h2{font-size:14px;margin:24px 0 8px;color:#6f0000;border-bottom:2px solid #6f0000;padding-bottom:4px}p{color:#666;font-size:13px;margin-bottom:20px}table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px}th{background:#6f0000;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px}td{padding:8px 12px;border-bottom:1px solid #f0f0f0}@media print{body{margin:16px}}</style></head><body><h1>TRIP Presentation Schedule</h1><p>Generated ${new Date().toLocaleString()}</p>${Object.entries(byRoom).map(([room,{topic,entries}])=>`<h2>${room} — ${topic}</h2><table><thead><tr><th>Time</th><th>Student</th><th>Group</th><th>Duration</th></tr></thead><tbody>${entries.sort((a,b)=>a.slot.localeCompare(b.slot)).map(e=>`<tr><td><b>${e.slot}</b></td><td>${e.firstName} ${e.lastName}</td><td>Group ${e.group}</td><td>${e.duration} min</td></tr>`).join("")}</tbody></table>`).join("")}</body></html>`;
                const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.focus(); w.print();
              }} style={{ background:"white",border:"1.5px solid #6f0000",color:"#6f0000",padding:"9px 18px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer" }}>Print / PDF</button>
            </div>
            {presentationSchedule.length === 0 ? (
              <div style={{ background:"white",borderRadius:14,padding:"40px",textAlign:"center",boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ fontWeight:700,fontSize:16,color:"#333",marginBottom:8 }}>No presentation schedule yet</div>
                <div style={{ color:"#9ca3af",fontSize:14 }}>The schedule will appear here once published.</div>
              </div>
            ) : (() => {
              const byRoom = {};
              presentationSchedule.forEach(e=>{ (byRoom[e.room]=byRoom[e.room]||{topic:e.topic,entries:[]}).entries.push(e); });
              return Object.entries(byRoom).map(([room,{topic,entries}])=>(
                <div key={room} style={{ background:"white",borderRadius:14,marginBottom:16,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ background:"linear-gradient(135deg,#6f0000,#a00000)",padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                    <div style={{ color:"white",fontWeight:800,fontSize:15 }}>{room}</div>
                    <div style={{ color:"rgba(255,255,255,0.7)",fontSize:13 }}>{topic} · {entries.length} presentation{entries.length!==1?"s":""}</div>
                  </div>
                  <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                    <thead><tr style={{ background:"#f8f8f8",borderBottom:"1px solid #f0f0f0" }}>{["Time","Student","Group","Duration"].map(h=><th key={h} style={{ textAlign:"left",padding:"8px 14px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.4 }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {entries.sort((a,b)=>a.slot.localeCompare(b.slot)).map((e,i)=>(
                        <tr key={i} style={{ borderBottom:"1px solid #f8f8f8",background:i%2===0?"white":"#fafafa" }}>
                          <td style={{ padding:"10px 14px",fontWeight:700,color:"#6f0000" }}>{e.slot}</td>
                          <td style={{ padding:"10px 14px",fontWeight:600,color:"#1a1a1a" }}>{e.firstName} {e.lastName}</td>
                          <td style={{ padding:"10px 14px" }}><span style={{ background:"#ede9fe",color:"#7c3aed",border:"1px solid #c4b5fd",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:700 }}>Group {e.group}</span></td>
                          <td style={{ padding:"10px 14px",color:"#888",fontSize:12 }}>{e.duration} min</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ));
            })()}
          </div>
        )}

        {activeTab === "candidates" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Candidate Preferences</h1>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>Select up to {maxPreferred} preferred candidates for interviews</p>
              </div>
              {submitted && !editMode && !isLockedOut && (
                <button onClick={() => setEditMode(true)} style={{ background: "white", border: `2px solid ${MAROON}`, color: MAROON, padding: "10px 20px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Edit Preferences</button>
              )}
            </div>
            {isLockedOut && (
              <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:12,padding:"16px 20px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:12 }}>
                <span style={{ fontSize:20,flexShrink:0 }}>🔒</span>
                <div>
                  <div style={{ fontWeight:700,fontSize:14,color:"#991b1b",marginBottom:4 }}>Preferences Locked</div>
                  <div style={{ fontSize:13,color:"#7f1d1d",lineHeight:1.6 }}>Your {profile.memberType?.replace(" Board Member","") || "board"} preferences have been locked by the admin. You can view your selections but cannot make changes.</div>
                </div>
              </div>
            )}
            {editMode && !isLockedOut && (
              <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                <button onClick={() => setEditMode(false)} style={{ background: "white", border: "1.5px solid #e0e0e0", color: "#555", padding: "10px 18px", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                <button onClick={handleSubmit} disabled={saving} style={btn}>{saving ? "Saving..." : "Resubmit →"}</button>
              </div>
            )}

            {/* Confidence feedback */}
            <div style={{ ...card, padding: "16px 22px", marginBottom: 18, background: preferences.length >= Math.round(maxPreferred/2) ? "#f0fdf4" : preferences.length >= Math.round(maxPreferred/4) ? "#fffbeb" : "#fff1f0", border: `1px solid ${preferences.length >= Math.round(maxPreferred/2) ? "#86efac" : preferences.length >= Math.round(maxPreferred/4) ? "#fcd34d" : "#fca5a5"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: preferences.length >= Math.round(maxPreferred/2) ? "#166534" : preferences.length >= Math.round(maxPreferred/4) ? "#92400e" : "#991b1b" }}>
                  {preferences.length >= Math.round(maxPreferred/2) ? `You've selected ${preferences.length} candidates — great for scheduling!` : preferences.length >= Math.round(maxPreferred/4) ? `You've selected ${preferences.length} candidates — recommend at least 10 for best results` : preferences.length === 0 ? "No candidates selected yet — select from the list below" : `You've selected ${preferences.length} candidates — select more for better scheduling`}
                </span>
                <span style={{ fontSize: 13, color: "#aaa", fontWeight: 600 }}>{preferences.length}/{maxPreferred} selected</span>
              </div>
            </div>

            {submitted && !editMode && (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", padding: "14px 18px", borderRadius: 12, marginBottom: 20, fontSize: 14 }}>
                Preferences submitted. You can still edit until the deadline.
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
              <div style={card}>
                {/* Stats */}
                <div style={{ display: "flex", gap: 12, marginBottom: 18, paddingBottom: 16, borderBottom: "2px solid #f0f0f0" }}>
                  {[
                    { label: "Selected", value: preferences.length, color: MAROON },
                    { label: "Remaining", value: maxPreferred - preferences.length, color: "#1d4ed8" },
                    { label: "Total Students", value: students.length, color: "#0f766e" },
                  ].map(s => (
                    <div key={s.label} style={{ background: "#f8f8f8", borderRadius: 10, padding: "10px 16px", flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search candidates by name..."
                  style={{ ...inp("srch", focused, setFocused), marginBottom: 10 }}
                  onFocus={() => setFocused("srch")} onBlur={() => setFocused(null)} />

                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  <select value={candidateGroupFilter} onChange={e => setCandidateGroupFilter(e.target.value)} style={{ ...inp("grpf", focused, setFocused), flex: 1 }}>
                    <option value="all">All Groups</option>
                    {interviewGroups.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                  <select value={candidateSort} onChange={e => setCandidateSort(e.target.value)} style={{ ...inp("srtf", focused, setFocused), flex: 1 }}>
                    <option value="default">Default Order</option>
                    <option value="alpha">Alphabetical (Last Name)</option>
                  </select>
                </div>

                <div style={{ maxHeight: 500, overflowY: "auto", paddingRight: 2 }}>
                  {filtered.map(s => {
                    const selected = preferences.find(p => p.id === s.id);
                    const canInteract = (!submitted || editMode) && !isLockedOut;
                    return (
                      <div key={s.id} className={canInteract ? "lift" : ""}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 8, background: selected ? "#fff0f0" : "#fafafa", border: `1.5px solid ${selected ? "#fca5a5" : "#f0f0f0"}`, transition: "all 0.15s" }}>
                        {s.photoURL
                          ? <img src={s.photoURL} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", border: `2px solid ${selected ? "#fca5a5" : "#e8e8e8"}`, flexShrink: 0 }} />
                          : <div style={{ width: 38, height: 38, borderRadius: "50%", background: selected ? "#ffeaea" : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, color: selected ? MAROON : "#bbb", flexShrink: 0, border: `2px solid ${selected ? "#fca5a5" : "#e8e8e8"}` }}>◎</div>
                        }
                        <div style={{ flex: 1, cursor: canInteract ? "pointer" : "default" }} onClick={() => canInteract && toggleStudent(s)}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a", marginBottom: 2 }}>{s.firstName} {s.lastName}</div>
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {s.undergradDegree || "Finance"} · <span style={{ background: "#fff0f0", color: MAROON, padding: "1px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Group {s.group}</span>
                            {s.classYear && <span style={{ marginLeft: 4, color: "#bbb" }}>· {s.classYear}</span>}
                            {(s.springInternshipCompany || s.summerInternshipCompany) && <span style={{ marginLeft: 4, color: "#bbb" }}>· {s.springInternshipCompany || s.summerInternshipCompany}</span>}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button onClick={() => setPreviewStudent(s)} style={{ background: "white", border: "1.5px solid #e8e8e8", color: "#555", padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}
                            onMouseOver={e => { e.currentTarget.style.borderColor = MAROON; e.currentTarget.style.color = MAROON; }}
                            onMouseOut={e => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.color = "#555"; }}>
                            View Profile
                          </button>
                          {canInteract && (
                            <button onClick={() => { if (window.confirm(`Block ${s.firstName} ${s.lastName}? They will no longer appear in your candidate list and can't be scheduled with your company.`)) toggleBlockStudent(s.id, `${s.firstName} ${s.lastName}`); }}
                              style={{ background: "white", border: "1.5px solid #fca5a5", color: "#dc2626", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                              Block
                            </button>
                          )}
                          <button onClick={() => setCompareList(prev => prev.find(p => p.id === s.id) ? prev.filter(p => p.id !== s.id) : prev.length < 2 ? [...prev, s] : prev)}
                            style={{ background: compareList.find(p => p.id === s.id) ? "#eff6ff" : "white", border: `1.5px solid ${compareList.find(p => p.id === s.id) ? "#93c5fd" : "#e8e8e8"}`, color: compareList.find(p => p.id === s.id) ? "#1d4ed8" : "#555", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: compareList.length >= 2 && !compareList.find(p => p.id === s.id) ? "not-allowed" : "pointer", transition: "all 0.15s" }}>
                            {compareList.find(p => p.id === s.id) ? "✓ Comparing" : "Compare"}
                          </button>
                          {selected ? (
                            <div style={{ width: 28, height: 28, background: `linear-gradient(135deg, ${MAROON}, #a00000)`, color: "white", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, cursor: canInteract ? "pointer" : "default", boxShadow: "0 2px 8px rgba(111,0,0,0.3)" }} onClick={() => canInteract && toggleStudent(s)}>
                              {selected.rank}
                            </div>
                          ) : canInteract && (
                            <div style={{ width: 28, height: 28, borderRadius: "50%", border: "2px dashed #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#d1d5db", cursor: "pointer" }} onClick={() => toggleStudent(s)}>+</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <div style={{ textAlign: "center", color: "#d1d5db", padding: 32 }}>No students found</div>}
                </div>
              </div>

              {/* Selections panel */}
              <div>
                <div style={card}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Your Rankings ({preferences.length}/{maxPreferred})</div>
                  {preferences.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "28px 0", color: "#d1d5db" }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>◎</div>
                      <div style={{ fontSize: 13 }}>Select candidates from the list</div>
                    </div>
                  ) : (
                    <>
                    {(!submitted || editMode) && !isLockedOut && (
                      <div style={{ fontSize: 11, color: "#bbb", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 13 }}>⠿</span> Drag to reorder
                      </div>
                    )}
                    <div style={{ maxHeight: 380, overflowY: "auto" }}>
                      {preferences.map((p, i) => {
                        const canDrag = (!submitted || editMode) && !isLockedOut;
                        return (
                          <div key={p.id}
                            draggable={canDrag}
                            onDragStart={() => setDragIdx(i)}
                            onDragOver={e => { e.preventDefault(); if (dragOverIdx !== i) setDragOverIdx(i); }}
                            onDrop={() => {
                              if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
                                setPreferences(prev => {
                                  const next = [...prev];
                                  const [removed] = next.splice(dragIdx, 1);
                                  next.splice(dragOverIdx, 0, removed);
                                  return next.map((x, idx) => ({ ...x, rank: idx + 1 }));
                                });
                                setUnsaved(true);
                              }
                              setDragIdx(null); setDragOverIdx(null);
                            }}
                            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 6px", borderRadius: 8, marginBottom: 2, background: dragIdx === i ? "rgba(111,0,0,0.04)" : dragOverIdx === i && dragIdx !== i ? "#fff0f0" : "transparent", borderTop: dragOverIdx === i && dragIdx !== i && dragIdx !== null ? `2px solid ${MAROON}` : "2px solid transparent", opacity: dragIdx === i ? 0.45 : 1, transition: "background 0.12s, opacity 0.12s", cursor: canDrag ? "grab" : "default" }}>
                            {canDrag && <span style={{ color: "#d1d5db", fontSize: 16, flexShrink: 0, userSelect: "none", letterSpacing: -2 }}>⠿</span>}
                            <div style={{ width: 24, height: 24, background: i < 5 ? `linear-gradient(135deg, ${MAROON}, #a00000)` : "#f0f0f0", color: i < 5 ? "white" : "#888", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#333", flex: 1, userSelect: "none" }}>{p.name}</span>
                            {canDrag && (
                              <span onClick={() => toggleStudent({ id: p.id, firstName: "", lastName: "" })} style={{ fontSize: 16, color: "#e0e0e0", cursor: "pointer", flexShrink: 0 }}
                                onMouseOver={e => e.currentTarget.style.color = "#dc2626"}
                                onMouseOut={e => e.currentTarget.style.color = "#e0e0e0"}>×</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    </>
                  )}
                  {(!submitted || editMode) && !isLockedOut && preferences.length > 0 && (
                    <button onClick={handleSubmit} disabled={saving} style={{ ...btn, width: "100%", justifyContent: "center", marginTop: 18 }}
                      onMouseOver={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(111,0,0,0.4)"; }}
                      onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(111,0,0,0.25)"; }}>
                      {saving ? "Submitting..." : editMode ? "Resubmit →" : "Submit Preferences →"}
                    </button>
                  )}
                </div>
                {blockedStudents.length > 0 && (
                  <div style={{ ...card, marginTop: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Blocked Students ({blockedStudents.length})</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14, lineHeight: 1.5 }}>Use this for students who have already interned with your company — blocking removes them from your candidate list and scheduler entirely.</div>
                    {blockedStudents.map(b => (
                      <div key={b.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#fff1f0", border: "1px solid #fca5a5", borderRadius: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: "#991b1b", fontWeight: 600 }}>{b.name}</span>
                        <button onClick={() => toggleBlockStudent(b.id, b.name)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Unblock</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "blockout" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 22 }}>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Block Out Times</h1>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Optional — only submit this if there are times during your interview day when you can't interview. Once an admin approves your request, it's applied to the scheduler so no student is placed with you during those slots.</p>
            </div>
            {blockoutRequestStatus === "pending" && (
              <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 12, padding: "14px 18px", marginBottom: 18, fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 10 }}>
                ⏳ Your request is pending admin approval.
              </div>
            )}
            {blockoutRequestStatus === "approved" && (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: "14px 18px", marginBottom: 18, fontSize: 13, color: "#166534", display: "flex", alignItems: "center", gap: 10 }}>
                ✓ Approved — these times have been applied to the scheduler. Editing and resubmitting will require re-approval.
              </div>
            )}
            {blockoutRequestStatus === "denied" && (
              <div style={{ background: "#fff1f0", border: "1px solid #fca5a5", borderRadius: 12, padding: "14px 18px", marginBottom: 18, fontSize: 13, color: "#991b1b", display: "flex", alignItems: "center", gap: 10 }}>
                ✗ Your last request was denied — adjust your times below and resubmit, or contact an admin.
              </div>
            )}
            <div style={card}>
              <BlackoutPicker value={blockoutTimes} onChange={v => { setBlockoutTimes(v); setUnsaved(true); }} settings={settings} />
            </div>
            <button onClick={saveBlockout} disabled={saving || blockoutTimes.length === 0} style={btn}>
              {saving ? "Submitting..." : "Submit for Approval →"}
            </button>
          </div>
        )}

      </>}
      </div>
      {/* Sticky candidate selection bar */}
      {activeTab === "candidates" && preferences.length > 0 && (!submitted || editMode) && !isLockedOut && (() => {
        const maxPicks = maxPreferred;
        const pct = Math.min((preferences.length / maxPicks) * 100, 100);
        const isGood = preferences.length >= Math.round(maxPreferred/2);
        return (
          <div style={{ position:"fixed",bottom:0,left:240,right:0,background:"#0f172a",borderTop:"1px solid rgba(255,255,255,0.08)",padding:"14px 40px",display:"flex",alignItems:"center",gap:20,zIndex:90,animation:"slideIn 0.25s ease" }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                <span style={{ color:"white",fontWeight:700,fontSize:14 }}>{preferences.length} candidate{preferences.length !== 1 ? "s" : ""} selected</span>
                <span style={{ color:"rgba(255,255,255,0.4)",fontSize:13 }}>max {maxPicks}</span>
              </div>
              <div style={{ background:"rgba(255,255,255,0.1)",borderRadius:3,height:4 }}>
                <div style={{ background:isGood?"#22c55e":MAROON,height:4,borderRadius:3,width:`${pct}%`,transition:"width 0.3s" }} />
              </div>
            </div>
            {isGood ? <span style={{ color:"#22c55e",fontSize:12,fontWeight:700,whiteSpace:"nowrap" }}>✓ Good selection</span> : <span style={{ color:"#f59e0b",fontSize:12,fontWeight:600,whiteSpace:"nowrap" }}>Recommend {Math.round(maxPreferred/2)}+</span>}
          </div>
        );
      })()}

      {/* Session timeout warning */}
      {sessionCountdown !== null && (
        <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#0f172a", border: "1px solid rgba(239,68,68,0.4)", color: "white", padding: "14px 24px", borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", gap: 14, zIndex: 300, animation: "slideIn 0.3s ease" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", animation: "pulse 1s ease infinite", flexShrink: 0 }} />
          Session expires in {Math.floor(sessionCountdown / 60)}:{String(sessionCountdown % 60).padStart(2, "0")}
          <button onClick={() => { signOut(auth); navigate("/august"); }} style={{ background: "#ef4444", color: "white", border: "none", padding: "5px 14px", borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: "pointer", marginLeft: 4 }}>Sign Out</button>
        </div>
      )}

      {/* Compare bar */}
      {compareList.length > 0 && (
        <div style={{ position: "fixed", bottom: 0, left: 240, right: 0, background: "#0f172a", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "12px 32px", display: "flex", alignItems: "center", gap: 16, zIndex: 90 }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>Comparing:</span>
          {compareList.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "6px 12px" }}>
              <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{s.firstName} {s.lastName}</span>
              <button onClick={() => setCompareList(prev => prev.filter(p => p.id !== s.id))} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
          {compareList.length === 2 ? (
            <button onClick={() => setShowCompare(true)} style={{ background: `linear-gradient(135deg,${MAROON},#a00000)`, color: "white", border: "none", padding: "8px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", marginLeft: "auto" }}>Compare Side by Side →</button>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, marginLeft: "auto" }}>Select one more to compare</span>
          )}
        </div>
      )}

      {/* Compare modal */}
      {showCompare && (
      <div style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 2000, alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(8px)" }}
        onClick={() => setShowCompare(false)}>
        <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 20, padding: "20px 24px", width: "100%", maxWidth: 1080, height: "86vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(0,0,0,0.5)", animation: "fadeUp 0.25s ease" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexShrink: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#0f0f0f" }}>Side-by-Side Comparison</div>
            <button onClick={() => setShowCompare(false)} style={{ background: "none", border: "1.5px solid #e8e8e8", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 15, color: "#aaa" }}>×</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, flex: 1, minHeight: 0 }}>
            {compareList.map(s => {
              const workEntries = [
                s.springInternshipCompany && { label: "Spring Internship", company: s.springInternshipCompany, desc: s.springInternship },
                s.summerInternshipCompany && { label: "Summer Internship", company: s.summerInternshipCompany, desc: s.summerInternship },
                s.investmentDecision && { label: "Commodities Analysis" + (s.presentationTopic ? ` (${s.presentationTopic})` : ""), company: null, desc: s.investmentDecision },
              ].filter(Boolean);
              const isExpanded = !!expandedStatements[s.id];
              return (
              <div key={s.id} style={{ background: "#f8f8f8", borderRadius: 14, padding: "16px 18px", border: `2px solid ${preferences.find(p => p.id === s.id) ? "#fca5a5" : "#f0f0f0"}`, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexShrink: 0 }}>
                  {s.photoURL ? <img src={s.photoURL} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `2px solid ${MAROON}` }} /> : <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#e8e8e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#bbb" }}>◎</div>}
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "#0f0f0f" }}>{s.firstName} {s.lastName}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>Group {s.group} · {s.classYear || "N/A"}</div>
                    {preferences.find(p => p.id === s.id) && <div style={{ fontSize: 11, color: MAROON, fontWeight: 700, marginTop: 1 }}>Rank #{preferences.find(p => p.id === s.id).rank}</div>}
                  </div>
                </div>

                <div style={{ overflowY: "auto", flex: 1, minHeight: 0, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Academic Info</div>
                  {[["Degree", s.undergradDegree], ["Hometown", s.hometown], ["Grad", s.expectedGraduation], ["GPA", s.gpa], ["Seeking", s.seekingMFM]].filter(([,v]) => v).map(([label, value]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid #eee", fontSize: 12 }}>
                      <span style={{ color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>{label}</span>
                      <span style={{ color: "#333", fontWeight: 500, textAlign: "right", overflowWrap: "anywhere" }}>{value}</span>
                    </div>
                  ))}

                  {workEntries.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>Work Experience</div>
                      {workEntries.map((w, i) => (
                        <div key={i} style={{ background: "white", borderRadius: 8, padding: "8px 11px", marginBottom: 6, overflowWrap: "anywhere" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#333" }}>{w.label}{w.company ? ` — ${w.company}` : ""}</div>
                          {w.desc && <div style={{ fontSize: 11, color: "#555", lineHeight: 1.5, marginTop: 3 }}>{w.desc}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {s.personalStatement && (
                    <div style={{ marginTop: 10, background: "white", borderRadius: 8, padding: "10px 12px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>Personal Statement</div>
                      <div style={isExpanded ? { fontSize: 11, color: "#555", lineHeight: 1.6, overflowWrap: "anywhere" } : { fontSize: 11, color: "#555", lineHeight: 1.6, overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.personalStatement}</div>
                      {s.personalStatement.length > 260 && (
                        <button onClick={() => setExpandedStatements(prev => ({ ...prev, [s.id]: !prev[s.id] }))} style={{ background: "none", border: "none", color: MAROON, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "4px 0 0" }}>
                          {isExpanded ? "Show less" : "Show full statement"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10, flexShrink: 0 }}>
                  {s.resumeURL && <a href={s.resumeURL} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: MAROON, textDecoration: "none", border: `1.5px solid ${MAROON}`, padding: "5px 11px", borderRadius: 7 }}>Open Resume →</a>}
                  <button onClick={() => { toggleStudent(s); setShowCompare(false); }} style={{ fontSize: 11, fontWeight: 700, padding: "5px 11px", borderRadius: 7, border: `1.5px solid ${preferences.find(p => p.id === s.id) ? "#86efac" : "#fca5a5"}`, background: preferences.find(p => p.id === s.id) ? "#f0fdf4" : "#fff0f0", color: preferences.find(p => p.id === s.id) ? "#166534" : MAROON, cursor: "pointer" }}>
                    {preferences.find(p => p.id === s.id) ? "✓ Selected" : "+ Select"}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Sticky unsaved */}
      {unsaved && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "#1a1a1a", color: "white", padding: "12px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: 10, zIndex: 200, animation: "slideIn 0.3s ease" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1.5s ease infinite" }} />
          Unsaved changes
          <button onClick={activeTab === "rsvp" ? saveRsvp : saveProfile} style={{ background: MAROON, color: "white", border: "none", padding: "6px 14px", borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: "pointer", marginLeft: 4 }}>Save Now</button>
        </div>
      )}
    </div>
  );
}
